import React from 'react';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { renderRich } from '../pricing/richText';
import { BG_GRAY, LIGHT_GRAY, PRIMARY_COLORS } from '../../common/colors';
import { CIEL_ORG_URL } from '../../common/constants';

// /tools/mapper's CIEL bridge section (ocl_online#431): how the Mapper reaches
// a target terminology through CIEL's curated maps, what CIEL is, the
// evaluation behind it and the plans that include it. The FAQ's CIEL answers
// link here by its id, so changing it breaks /tools/mapper#ciel-bridge.

// The steps are an ordered list; each one but the last draws the arrow to
// the next in the gap after it, across on desktop and down on a phone.
const Step = ({ number, title, text, last, highlight }) => (
  <Box
    component="li"
    sx={{
      position: 'relative',
      listStyle: 'none',
      textAlign: 'center',
      p: 2.5,
      borderRadius: '10px',
      border: '1px solid',
      borderColor: highlight ? 'primary.main' : LIGHT_GRAY,
      backgroundColor: highlight ? PRIMARY_COLORS['95'] : 'common.white',
    }}
  >
    <Typography variant="body2" sx={{ color: 'primary.main', fontWeight: 700 }}>
      {number}
    </Typography>
    <Typography sx={{ color: 'surface.dark', fontWeight: 700, mt: 0.5 }}>
      {title}
    </Typography>
    <Typography variant="body2" sx={{ color: 'surface.contrastText', mt: 0.5 }}>
      {text}
    </Typography>
    {!last && (
      <ArrowForwardIcon
        sx={{
          position: 'absolute',
          color: 'primary.main',
          top: { xs: 'auto', md: '50%' },
          bottom: { xs: -32, md: 'auto' },
          left: { xs: '50%', md: 'auto' },
          right: { xs: 'auto', md: -32 },
          transform: { xs: 'translateX(-50%) rotate(90deg)', md: 'translateY(-50%)' },
        }}
      />
    )}
  </Box>
);

const CielBridge = ({ eyebrow, title, text, steps, names, evidence, plans, ciel }) => (
  <Box
    component="section"
    id="ciel-bridge"
    aria-labelledby="ciel-bridge-heading"
    sx={{ borderTop: `1px solid ${LIGHT_GRAY}`, scrollMarginTop: '96px' }}
  >
    <Container maxWidth="lg" sx={{ py: { xs: 6, md: 8 } }}>
      <Box sx={{ maxWidth: 760 }}>
        <Typography
          variant="body2"
          sx={{ color: 'primary.main', fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase' }}
        >
          {eyebrow}
        </Typography>
        <Typography id="ciel-bridge-heading" variant="h4" component="h2" sx={{ color: 'surface.dark', mt: 1.25, mb: 2 }}>
          {title}
        </Typography>
        <Typography sx={{ color: 'surface.contrastText' }}>
          {text}
        </Typography>
      </Box>
      <Box
        component="ol"
        sx={{ m: 0, mt: 4, p: 0, display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' }, gap: 5 }}
      >
        {steps.map((step, i) => (
          <Step key={step.title} number={i + 1} last={i === steps.length - 1} highlight={i === 1} {...step} />
        ))}
      </Box>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '3fr 2fr' },
          gap: { xs: 4, md: 6 },
          alignItems: 'start',
          mt: { xs: 5, md: 6 },
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          <Typography sx={{ color: 'surface.contrastText' }}>
            {renderRich(names)}
          </Typography>
          <Typography sx={{ color: 'surface.dark', fontWeight: 600, borderLeft: '3px solid', borderColor: 'primary.main', pl: 2.5 }}>
            {evidence}
          </Typography>
          <Typography sx={{ color: 'surface.contrastText' }}>
            {renderRich(plans)}
          </Typography>
        </Box>
        <Box sx={{ backgroundColor: BG_GRAY, borderRadius: '10px', p: { xs: 3, md: 3.5 } }}>
          <Typography variant="h6" component="h3" sx={{ color: 'surface.dark', mb: 1 }}>
            {ciel.title}
          </Typography>
          <Typography variant="body2" sx={{ color: 'surface.contrastText', mb: 2 }}>
            {ciel.text}
          </Typography>
          <Link href={CIEL_ORG_URL} sx={{ fontWeight: 600 }}>
            {ciel.link} →
          </Link>
        </Box>
      </Box>
    </Container>
  </Box>
);

export default CielBridge;
